import {
  type CadenceConnexion,
  codeDefiValide,
  type EtatConnexionPortail,
  formaterJour,
  formaterPeriode,
  jourAParis,
  LIBELLES_DEFI,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, ErreurApi, lire, requeteMoi, requetes } from '../api'
import { LIBELLES_CADENCE } from '../libelles'
import {
  LIBELLES_MODE_PASSAGE,
  LIBELLES_STATUT_PASSAGE,
  messageRapprochement,
  relancePossible,
  resumeComptes,
  resumeEcritures,
  TEXTE_PASSAGE_MENSUEL,
} from '../composants/bilan-justificatifs'
import { Decompte } from '../composants/Decompte'
import { formaterInstant } from '../composants/Historique'
import { Logo } from '../composants/Logo'
import { textesDefiLocal } from '../defi-local'
import { Section, Vide } from '../composants/Section'
import { confirmer } from '../composants/confirmation'

// Réglages, « Portails » : connexions aux espaces clients des fournisseurs, relevées par le
// collecteur (navigateur sans modèle, identifiants dans Bitwarden). On n'y voit jamais un mot de
// passe, une session ni un code ; on y saisit le code que le fournisseur vient d'envoyer, on
// guette une validation, on lance ou relance une collecte (plus rien ne part seul : page
// « Collecteurs », décision du 21/10/2026). Lecture par entité ; ajout,
// modification et suppression par un administrateur. Un portail de justificatifs du cabinet
// (Tiime) ne relève pas de documents : on y rapproche les opérations des pièces (« Rapprocher
// maintenant », passage mensuel), le bilan est sur sa page.

type Reponse = InferResponseType<typeof api.portails.$get, 200>
type Connexion = Reponse['connexions'][number]
type Defi = NonNullable<Connexion['defi']>
type ResumeJustificatifs = NonNullable<Connexion['justificatifs']>['dernier']

/** Nom court du portail (« Tiime »), d'après le libellé du fournisseur. */
const nomCourt = (libelle: string) => libelle.split(' (')[0] ?? libelle

/**
 * Le dernier passage d'un portail de justificatifs, en une ligne de nombres : ce que le plan a
 * trouvé (« 312 opérations lues »), puis ce que l'agent a écrit (« 9 reliées ce passage »).
 */
function texteDernierPassage(d: NonNullable<ResumeJustificatifs>, ecritureOuverte: boolean) {
  return [
    `Dernier passage le ${formaterInstant(d.creeLe)}`,
    d.mode ? LIBELLES_MODE_PASSAGE[d.mode].toLowerCase() : null,
    LIBELLES_STATUT_PASSAGE[d.statut],
    ...resumeComptes(
      { lues: d.lues, etats: d.bilan?.etats ?? null, marquees: d.bilan?.marquees },
      { ecritureOuverte },
    ),
    ...resumeEcritures(d.ecritures),
  ]
    .filter(Boolean)
    .join(' · ')
}

const ETATS: Record<EtatConnexionPortail, { libelle: string; classe: string }> = {
  jamais: { libelle: 'Jamais collectée', classe: '' },
  ok: { libelle: 'OK', classe: '' },
  verification_requise: { libelle: 'Vérification requise', classe: 'retard-en_retard' },
  erreur: { libelle: 'Erreur', classe: 'retard-en_retard' },
  bloquee: { libelle: 'Bloquée', classe: 'retard-en_retard' },
}

// Qui relève la connexion (`releveConnexion` dans le cœur) ; rien : elle attend son titulaire.
const LIEUX_RELEVE: Record<Connexion['releve'], (ordinateur: string | null) => string> = {
  serveur: () => 'Relevée par le collecteur du serveur',
  ordinateur: (o) => `Relevée sur ${o ?? 'un ordinateur'}`,
  a_relier: () => 'Relevée par aucun ordinateur',
  a_reattribuer: (o) => `Liée à ${o ?? 'un ordinateur'}, révoqué`,
}
const LIBELLES_SANS_RELEVE: Record<Connexion['releve'], string> = {
  serveur: '',
  ordinateur: '',
  a_relier: 'À relier',
  a_reattribuer: 'À réattribuer',
}

// Bloquée par l'une de ces étapes : l'humain s'en charge (vue en direct à la prochaine collecte
// lancée à la main, ou de son côté pour une clé d'accès).
const IMPOSSIBLES = new Set([
  'passkey',
  'webauthn',
  'captcha',
  'connexion_interactive',
  'consentement',
  'changement_mot_de_passe',
])

function Verification({
  connexion,
  defi,
  titulaire,
}: {
  connexion: Connexion
  defi: Defi
  titulaire: boolean
}) {
  const client = useQueryClient()
  const [code, setCode] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const rafraichir = () => client.invalidateQueries({ queryKey: ['portails'] })
  const agir = async (appel: () => Promise<unknown>) => {
    setErreur(null)
    setOccupe(true)
    try {
      await appel()
      await rafraichir()
    } catch (x) {
      setErreur(x instanceof ErreurApi ? x.message : 'Action impossible.')
    } finally {
      setOccupe(false)
    }
  }
  const annuler = (
    <>
      <button
        type="button"
        className="bouton-discret"
        disabled={occupe}
        onClick={() =>
          agir(() => lire(api.portails.defis[':id'].annuler.$post({ param: { id: defi.id } })))
        }
      >
        Pas maintenant
      </button>
      <Link to="/defi/$id" params={{ id: defi.id }} className="bouton-discret">
        Plein écran
      </Link>
    </>
  )

  // Seul le titulaire reçoit le code et répond ; les autres voient l'état.
  if (!titulaire)
    return (
      <div className="section-ajout" role="status">
        <strong>Vérification nécessaire</strong>
        <p>
          {connexion.libelle} attend {connexion.titulaire} ({defi.libelle}
          {defi.enDirect ? '' : ', connexion à relancer'}).
        </p>
      </div>
    )

  // Collecteur local : tout se fait sur l'ordinateur (fenêtre, ou onglet prêté de Chrome pour un
  // portail de justificatifs), rien ne se saisit ici. La consigne de l'agent passe d'abord :
  // extension à relier, passage d'une autre personne qui attend le « C'est parti » du titulaire.
  if (defi.conduite.mode === 'local') {
    const textes = textesDefiLocal({
      message: defi.message,
      libelle: defi.libelle,
      ongletPrete: connexion.justificatifs !== null,
      ou: connexion.ordinateur ?? 'votre ordinateur',
    })
    return (
      <div className="section-ajout" role="status">
        <strong>Connexion à faire sur {connexion.ordinateur ?? 'votre ordinateur'}</strong>
        <p>
          {defi.enDirect
            ? `${textes.ouvert} (${defi.libelle}) : connectez-vous-y vous-même.`
            : `${connexion.libelle} : ${textes.attente}`}
        </p>
        {defi.enDirect && (
          <p>
            <Decompte jusqua={defi.expireLe} />
          </p>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
        <div className="boutons">
          {!defi.enDirect && (
            <button
              type="button"
              className="bouton"
              disabled={occupe}
              onClick={() =>
                agir(() =>
                  lire(api.portails.defis[':id'].continuer.$post({ param: { id: defi.id } })),
                )
              }
            >
              C’est parti
            </button>
          )}
          {annuler}
        </div>
      </div>
    )
  }

  if (!defi.enDirect)
    return (
      <div className="section-ajout" role="status">
        <strong>Vérification nécessaire</strong>
        <p>
          {connexion.libelle} demandera une vérification ({defi.libelle}). Lancez la connexion quand
          vous pouvez y répondre : le code arrivera dans la minute.
        </p>
        {erreur && <p className="alerte">{erreur}</p>}
        <div className="boutons">
          <button
            type="button"
            className="bouton"
            disabled={occupe}
            onClick={() =>
              agir(() =>
                lire(api.portails.defis[':id'].continuer.$post({ param: { id: defi.id } })),
              )
            }
          >
            Continuer maintenant
          </button>
          {annuler}
        </div>
      </div>
    )

  if (defi.conduite.mode === 'vue')
    return (
      <div className="section-ajout" role="status">
        <strong>Vérification nécessaire</strong>
        <p>
          {connexion.libelle} demande : {defi.libelle}. Franchissez l’étape vous-même dans la vue en
          direct du navigateur du robot.
        </p>
        <p>
          <Decompte jusqua={defi.expireLe} />
        </p>
        {erreur && <p className="alerte">{erreur}</p>}
        <div className="boutons">
          <Link to="/defi/$id" params={{ id: defi.id }} className="bouton">
            Ouvrir la vue en direct
          </Link>
          {annuler}
        </div>
      </div>
    )

  if (defi.conduite.mode === 'attente')
    return (
      <div className="section-ajout" role="status">
        <strong>Vérification nécessaire</strong>
        <p>{connexion.libelle} : validez la connexion dans l’application du fournisseur.</p>
        <p className="discret">
          En attente de validation… <Decompte jusqua={defi.expireLe} />
        </p>
        {erreur && <p className="alerte">{erreur}</p>}
        <div className="boutons">{annuler}</div>
      </div>
    )

  return (
    <form
      className="section-ajout"
      onSubmit={(e) => {
        e.preventDefault()
        const propre = codeDefiValide(code)
        if (!propre) {
          setErreur('Code illisible : 4 à 12 chiffres ou lettres.')
          return
        }
        void agir(async () => {
          await lire(
            api.portails.defis[':id'].code.$post({
              param: { id: defi.id },
              json: { code: propre },
            }),
          )
          setCode('')
        })
      }}
    >
      <strong>Vérification nécessaire</strong>
      <p>
        {connexion.libelle} : {defi.message ?? LIBELLES_DEFI[defi.type]}
      </p>
      {defi.repondu ? (
        <p className="discret">Code transmis, connexion en cours…</p>
      ) : (
        <div className="formulaire">
          <div className="champ">
            <label htmlFor={`code-${defi.id}`}>Code reçu</label>
            <input
              id={`code-${defi.id}`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={20}
              required
            />
          </div>
        </div>
      )}
      <p>
        <Decompte jusqua={defi.expireLe} />
      </p>
      {erreur && <p className="alerte">{erreur}</p>}
      <div className="boutons">
        {!defi.repondu && (
          <button type="submit" className="bouton" disabled={occupe}>
            Continuer
          </button>
        )}
        {annuler}
      </div>
    </form>
  )
}

function Historique({ id }: { id: string }) {
  const collectes = useQuery({
    queryKey: ['portails', id, 'collectes'],
    queryFn: () => lire(api.portails[':id'].collectes.$get({ param: { id } })),
  })
  if (!collectes.data) return null
  if (collectes.data.length === 0) return <Vide>Aucune collecte.</Vide>
  return (
    <ul className="discret">
      {collectes.data.map((c) => (
        <li key={c.id}>
          {formaterInstant(c.creeLe)} · {c.declencheur === 'manuel' ? 'demandée' : 'planifiée'} ·{' '}
          {c.statut.replace('_', ' ')}
          {/* Passage de justificatifs : son mode et ses nombres, pas de documents. */}
          {c.justificatifs
            ? [
                c.justificatifs.mode
                  ? LIBELLES_MODE_PASSAGE[c.justificatifs.mode].toLowerCase()
                  : null,
                ...resumeComptes(
                  {
                    lues: c.justificatifs.lues,
                    etats: c.justificatifs.bilan?.etats ?? null,
                    marquees: c.justificatifs.bilan?.marquees,
                  },
                  { ecritureOuverte: c.justificatifs.mode === 'ecriture' },
                ),
                ...resumeEcritures(c.justificatifs.ecritures),
              ]
                .filter(Boolean)
                .map((x) => ` · ${x}`)
                .join('')
            : c.statut === 'terminee' &&
              ` · ${c.documentsTrouves} document(s), ${c.documentsNouveaux} nouveau(x)`}
          {c.erreur && ` · ${c.erreur}`}
        </li>
      ))}
    </ul>
  )
}

function LigneConnexion({
  connexion,
  administrateur,
  moi,
  onMessage,
}: {
  connexion: Connexion
  administrateur: boolean
  moi: string
  onMessage: (m: string) => void
}) {
  const client = useQueryClient()
  const [historique, setHistorique] = useState(false)
  const etat = ETATS[connexion.etat]
  const rafraichir = () => client.invalidateQueries({ queryKey: ['portails'] })
  const agir = async (appel: () => Promise<unknown>, reussite?: string) => {
    try {
      await appel()
      if (reussite) onMessage(reussite)
    } catch (x) {
      onMessage(x instanceof ErreurApi ? x.message : 'Action impossible.')
    } finally {
      await rafraichir()
    }
  }
  const enCours = connexion.collecte && !connexion.defi
  const sansReleve = connexion.active ? connexion.sansReleve : null
  const bloqueeParDefi =
    connexion.etat === 'bloquee' && connexion.dernierDefi && IMPOSSIBLES.has(connexion.dernierDefi)
  // Portail de justificatifs du cabinet (Tiime) : on y rapproche les opérations des pièces, on
  // n'y relève pas de documents ; passage mensuel ou à la demande, bilan sur sa page.
  const justificatifs = connexion.justificatifs
  const portail = nomCourt(connexion.fournisseurLibelle)
  const relancer = justificatifs ? 'Rapprocher maintenant' : 'Collecter maintenant'
  return (
    <div className="carte-banque">
      <div className="ligne">
        <div className="ligne-contenu">
          <div className="titre-logo">
            <Logo cle={connexion.fournisseur} nom={connexion.libelle} />
            <strong>{connexion.libelle}</strong>
          </div>
          <span className={`badge ${sansReleve ? 'retard-en_retard' : etat.classe}`}>
            {!connexion.active
              ? 'Désactivée'
              : sansReleve
                ? LIBELLES_SANS_RELEVE[connexion.releve]
                : etat.libelle}
          </span>
          <div className="discret">
            {connexion.entite ?? 'Sans entité'} · {connexion.fournisseurLibelle} · Web · titulaire{' '}
            {connexion.titulaire}
            {connexion.referenceSecret && ` · secret ${connexion.referenceSecret}`}
          </div>
          <div className="discret">
            {LIEUX_RELEVE[connexion.releve](connexion.ordinateur)}
            {connexion.cadence !== 'inconnue' &&
              ` · documents : ${LIBELLES_CADENCE[connexion.cadence].toLowerCase()}`}
          </div>
          {connexion.moisManquants.length > 0 && (
            <div className="discret">
              Manque : {connexion.moisManquants.map(formaterPeriode).join(', ')}
            </div>
          )}
          {connexion.moisAVenir.length > 0 && (
            <div className="discret">
              Facture à venir : {connexion.moisAVenir.map(formaterPeriode).join(', ')}
            </div>
          )}
          {sansReleve && <p className="alerte">{sansReleve}</p>}
          {connexion.titulaireId === moi && <ChoixOrdinateur connexion={connexion} />}
          {!connexion.robot && (
            <div className="discret">
              Pas encore de collecte automatique
              {connexion.releve === 'ordinateur'
                ? ` : déposez les fichiers dans le dossier « ${connexion.libelle} » d’Admin PMVB sur ${connexion.ordinateur}.`
                : ' : les fichiers se déposent sur l’ordinateur lié.'}
            </div>
          )}
          <div className="discret">
            {connexion.dernierSuccesLe
              ? `Dernier succès le ${formaterInstant(connexion.dernierSuccesLe)}`
              : 'Pas encore de succès'}
            {connexion.active &&
              !sansReleve &&
              !connexion.collecte &&
              (justificatifs
                ? ` · ${TEXTE_PASSAGE_MENSUEL}`
                : connexion.prochaineCollecteLe &&
                  ` · à relancer à partir du ${formaterJour(jourAParis(new Date(connexion.prochaineCollecteLe)))} (page Collecteurs)`)}
            {connexion.session ? ' · session gardée' : ''}
          </div>
          {justificatifs && (
            <>
              <div className="discret">
                {justificatifs.dernier
                  ? texteDernierPassage(justificatifs.dernier, connexion.ecritureActive)
                  : 'Aucun passage de rapprochement encore'}
              </div>
              <div className="discret">
                {connexion.passageValideLe
                  ? `Passage à blanc validé le ${formaterInstant(connexion.passageValideLe)}`
                  : 'Passage à blanc à valider par le titulaire'}
                {connexion.ecritureActive
                  ? ` · écriture ouverte dans ${portail}`
                  : ` · rien n’est écrit dans ${portail}`}
              </div>
            </>
          )}
          {enCours && (
            <div className="discret">
              {justificatifs ? 'Rapprochement en cours…' : 'Collecte en cours…'}
            </div>
          )}
          {connexion.derniereErreur && (
            <div className="discret">Dernière erreur : {connexion.derniereErreur}</div>
          )}
          {bloqueeParDefi && (
            <p className="alerte">
              Le portail demande : {LIBELLES_DEFI[connexion.dernierDefi!]}. Le serveur ne peut pas
              le faire seul : pour un CAPTCHA, une connexion à faire soi-même ou des conditions, «{' '}
              {relancer} » ouvre la vue en direct (si elle est activée) ; sinon, connectez-vous
              vous-même (consigne de l’agent navigateur), puis relancez.
            </p>
          )}
        </div>
        <div className="boutons">
          {justificatifs && (
            <Link to="/justificatifs/$id" params={{ id: connexion.id }} className="bouton-discret">
              Justificatifs
            </Link>
          )}
          {relancePossible(connexion) && (
            <button
              type="button"
              className="bouton-discret"
              onClick={() =>
                agir(
                  () => lire(api.portails[':id'].collecter.$post({ param: { id: connexion.id } })),
                  justificatifs
                    ? messageRapprochement(connexion, { moi, portail })
                    : `Collecte de ${connexion.libelle} demandée.`,
                )
              }
            >
              {relancer}
            </button>
          )}
          {connexion.session && (
            <button
              type="button"
              className="bouton-discret"
              onClick={async () => {
                if (
                  await confirmer(
                    `Oublier la session de ${connexion.libelle} ? La prochaine collecte se reconnectera.`,
                  )
                )
                  void agir(() =>
                    lire(
                      api.portails[':id']['oublier-session'].$post({ param: { id: connexion.id } }),
                    ),
                  )
              }}
            >
              Oublier la session
            </button>
          )}
          {administrateur && (
            <button
              type="button"
              className="bouton-discret"
              onClick={() =>
                agir(() =>
                  lire(
                    api.portails[':id'].$patch({
                      param: { id: connexion.id },
                      json: { active: !connexion.active },
                    }),
                  ),
                )
              }
            >
              {connexion.active ? 'Désactiver' : 'Réactiver'}
            </button>
          )}
          {administrateur && (
            <button
              type="button"
              className="bouton-discret"
              onClick={async () => {
                if (
                  await confirmer(
                    `Supprimer la connexion ${connexion.libelle} ? Les pièces relevées restent ; le secret reste dans Bitwarden.`,
                  )
                )
                  void agir(() =>
                    lire(api.portails[':id'].$delete({ param: { id: connexion.id } })),
                  )
              }}
            >
              Supprimer
            </button>
          )}
          <button type="button" className="bouton-discret" onClick={() => setHistorique((h) => !h)}>
            {historique ? 'Masquer l’historique' : 'Historique'}
          </button>
        </div>
      </div>
      {connexion.defi && (
        <Verification
          connexion={connexion}
          defi={connexion.defi}
          titulaire={connexion.titulaireId === moi}
        />
      )}
      {historique && <Historique id={connexion.id} />}
    </div>
  )
}

/**
 * Le titulaire choisit où la connexion est relevée : un de ses ordinateurs, ou le serveur là où
 * il la relève vraiment (ni compte sensible, ni dépôt de fichiers).
 */
function ChoixOrdinateur({ connexion }: { connexion: Connexion }) {
  const client = useQueryClient()
  const ordinateurs = (useQuery(requetes.ordinateurs).data ?? []).filter((o) => !o.revoqueLe)
  const [erreur, setErreur] = useState<string | null>(null)
  const aAjouter = ordinateurs.length === 0 && (
    <p className="discret">
      Aucun ordinateur appairé : ajoutez d’abord celui-ci dans le sous-onglet « Ordinateurs ».
    </p>
  )
  if (ordinateurs.length === 0 && !connexion.agentId)
    return connexion.serveurPossible ? null : aAjouter
  return (
    <div className="champ">
      <label htmlFor={`ordinateur-${connexion.id}`}>Relever sur</label>
      <select
        id={`ordinateur-${connexion.id}`}
        value={connexion.agentId ?? ''}
        onChange={async (e) => {
          setErreur(null)
          try {
            await lire(
              api.portails[':id'].ordinateur.$post({
                param: { id: connexion.id },
                json: { agentId: e.target.value || null },
              }),
            )
          } catch (x) {
            setErreur(x instanceof ErreurApi ? x.message : 'Changement impossible.')
          } finally {
            await client.invalidateQueries({ queryKey: ['portails'] })
            await client.invalidateQueries({ queryKey: ['ordinateurs'] })
          }
        }}
      >
        {connexion.serveurPossible ? (
          <option value="">Le serveur (identifiants dans Bitwarden)</option>
        ) : (
          !connexion.agentId && (
            <option value="" disabled>
              Choisir un ordinateur…
            </option>
          )
        )}
        {connexion.releve === 'a_reattribuer' && connexion.agentId && (
          <option value={connexion.agentId} disabled>
            {connexion.ordinateur} (révoqué)
          </option>
        )}
        {ordinateurs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.libelle} (je me connecte moi-même)
          </option>
        ))}
      </select>
      {aAjouter}
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}

function NouvelleConnexion({
  fournisseurs,
  fermer,
}: {
  fournisseurs: Reponse['fournisseurs']
  fermer: () => void
}) {
  const client = useQueryClient()
  const entites = (useQuery(requetes.entites).data ?? []).filter((e) => !e.archivee)
  const personnes = (useQuery(requetes.personnes).data ?? []).filter((p) => p.type === 'humain')
  const [d, setD] = useState({
    fournisseur: fournisseurs[0]?.cle ?? '',
    libelle: '',
    entiteId: '',
    referenceSecret: 'PORTAIL_',
    titulaireId: '',
    frequenceJours: 7,
    cadence: 'inconnue' as CadenceConnexion,
  })
  const [erreur, setErreur] = useState<string | null>(null)
  const choisi = fournisseurs.find((f) => f.cle === d.fournisseur)
  const robot = choisi?.robot ?? true
  // Compte sensible : aucun identifiant confié, le titulaire se connecte lui-même sur son ordinateur.
  const sensible = choisi?.sensible ?? false
  const avecSecret = robot && !sensible
  // Portail de justificatifs du cabinet : une entité avec SIREN, ni rythme ni fréquence (passage
  // mensuel ou à la demande, fixés par le serveur).
  const justificatifs = (choisi?.justificatifs ?? null) !== null
  const champ = (k: keyof typeof d) => ({
    id: `portail-${k}`,
    value: d[k],
    onChange: (e: { target: { value: string } }) =>
      setD({ ...d, [k]: k === 'frequenceJours' ? Number(e.target.value) : e.target.value }),
  })
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault()
        setErreur(null)
        try {
          await lire(
            api.portails.$post({
              json: {
                fournisseur: d.fournisseur as 'factice',
                libelle: d.libelle,
                entiteId: d.entiteId || null,
                referenceSecret: (avecSecret && d.referenceSecret.trim()) || null,
                titulaireId: d.titulaireId,
                frequenceJours: d.frequenceJours,
                cadence: justificatifs ? 'inconnue' : d.cadence,
                tiersId: null,
              },
            }),
          )
          await client.invalidateQueries({ queryKey: ['portails'] })
          fermer()
        } catch (x) {
          setErreur(x instanceof ErreurApi ? x.message : 'Connexion non enregistrée.')
        }
      }}
    >
      {justificatifs ? (
        <p className="discret">
          Portail de justificatifs du cabinet : aucun identifiant n’est confié à l’appli. Une seule
          connexion par entité, qui doit avoir un SIREN (son dossier y est choisi par lui). Liez la
          connexion à votre ordinateur : vous vous connectez vous-même dans votre Chrome de tous les
          jours (extension Admin PMVB), l’agent du Mac y lit les opérations et les rapproche des
          pièces quand vous lancez le passage (« Rapprocher maintenant » ; le passage du mois est
          mis en avant dans Collecteurs dès le 2). Premier passage à blanc, à valider par le
          titulaire : rien n’est écrit chez le portail.
        </p>
      ) : sensible ? (
        <p className="discret">
          Compte sensible : aucun identifiant n’est confié à l’appli. Liez la connexion à votre
          ordinateur ; vous vous y connecterez vous-même dans votre Chrome de tous les jours
          (extension Admin PMVB) ou, à défaut, dans la fenêtre du collecteur ; celui-ci ne fait
          ensuite que consulter et télécharger.
          {!robot && ' En attendant son adaptateur, déposez les fichiers dans son dossier.'}
        </p>
      ) : robot ? (
        <p className="discret">
          Les identifiants vont dans Bitwarden (projet du collecteur), jamais ici : saisissez le nom
          du secret, comme PORTAIL_EDF_SCI.
        </p>
      ) : (
        <p className="discret">
          Pas encore de collecte automatique pour ce portail : liez la connexion à votre ordinateur,
          puis déposez les fichiers téléchargés dans son dossier (Admin PMVB, puis le libellé de la
          connexion).
        </p>
      )}
      <div className="formulaire">
        <div className="champ">
          <label htmlFor="portail-fournisseur">Portail</label>
          <select {...champ('fournisseur')} required>
            {fournisseurs.map((f) => (
              <option key={f.cle} value={f.cle}>
                {f.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="portail-libelle">Libellé</label>
          <input {...champ('libelle')} required maxLength={120} />
        </div>
        <div className="champ">
          <label htmlFor="portail-entiteId">Entité</label>
          <select {...champ('entiteId')} required={justificatifs}>
            <option value="">
              {justificatifs ? 'Choisir l’entité du dossier…' : 'Sans entité (administrateurs)'}
            </option>
            {entites.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        </div>
        {avecSecret && (
          <div className="champ">
            <label htmlFor="portail-referenceSecret">Nom du secret Bitwarden</label>
            <input {...champ('referenceSecret')} autoComplete="off" spellCheck={false} />
          </div>
        )}
        <div className="champ">
          <label htmlFor="portail-titulaireId">Reçoit les codes</label>
          <select {...champ('titulaireId')} required>
            <option value="">Choisir…</option>
            {personnes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nom}
              </option>
            ))}
          </select>
        </div>
        {!justificatifs && (
          <>
            <div className="champ">
              <label htmlFor="portail-frequenceJours">Tous les (jours)</label>
              <input {...champ('frequenceJours')} type="number" min={1} max={31} />
            </div>
            <div className="champ">
              <label htmlFor="portail-cadence">Le fournisseur publie</label>
              <select {...champ('cadence')}>
                {Object.entries(LIBELLES_CADENCE).map(([cle, libelle]) => (
                  <option key={cle} value={cle}>
                    {libelle}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
      <div className="boutons">
        <button type="submit" className="bouton">
          Enregistrer
        </button>
        <button type="button" className="bouton-discret" onClick={fermer}>
          Annuler
        </button>
      </div>
    </form>
  )
}

export function Portails({ administrateur }: { administrateur: boolean }) {
  const moi = useSuspenseQuery(requeteMoi).data
  const [message, setMessage] = useState<string | null>(null)
  const reponse = useQuery({
    ...requetes.portails,
    // Plus souvent quand une collecte tourne ou qu'une vérification attend.
    refetchInterval: (q) =>
      q.state.data?.connexions.some((c) => c.collecte || c.defi) ? 3000 : 30_000,
  })
  const connexions = reponse.data?.connexions ?? []
  const compte = (etats: EtatConnexionPortail[]) =>
    connexions.filter((c) => c.active && !c.sansReleve && etats.includes(c.etat)).length
  const aRelier = connexions.filter((c) => c.active && c.sansReleve).length
  return (
    <Section
      titre="Portails"
      libelleAjout="Ajouter une connexion"
      ajout={
        administrateur && reponse.data
          ? (fermer) => (
              <NouvelleConnexion fournisseurs={reponse.data.fournisseurs} fermer={fermer} />
            )
          : undefined
      }
    >
      {reponse.data && !reponse.data.configuree && (
        <p className="discret">
          Collecte web non configurée (portails.cle_defis) : les codes ne peuvent pas être saisis.
        </p>
      )}
      {connexions.length > 0 && (
        <p className="discret">
          {connexions.length} connexion(s) · {compte(['ok'])} OK ·{' '}
          {compte(['verification_requise'])} vérification(s) requise(s) ·{' '}
          {compte(['erreur', 'bloquee'])} en erreur
          {aRelier > 0 && ` · ${aRelier} à relier à un ordinateur`}
        </p>
      )}
      {message && (
        <p className="discret" role="status">
          {message}
        </p>
      )}
      {reponse.data && connexions.length === 0 && <Vide>Aucune connexion de portail.</Vide>}
      {connexions.map((c) => (
        <LigneConnexion
          key={c.id}
          connexion={c}
          administrateur={administrateur}
          moi={moi.id}
          onMessage={setMessage}
        />
      ))}
    </Section>
  )
}
